import { useEffect, useState } from "react";
import { Title, useNotify, usePermissions } from "react-admin";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  InputLabel,
  FormControl,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

import {
  listProducts,
  getProduct,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadProductImages,
  deleteProductImages,
  getImageUrl,
} from "../../utils/salatMahsoolApi";
import { ProductStatusChip, useSalatCategories, useUnits, useStates, canWrite, canDelete } from "./common";

const emptyForm = {
  name: "",
  name_en: "",
  description: "",
  category_id: "",
  unit_id: "",
  state_id: "",
  price: "",
  quantity: "",
  status: "Active",
};

const ProductFormDialog = ({ open, onClose, productId, onSaved, categories, units, states }) => {
  const notify = useNotify();
  const [form, setForm] = useState(emptyForm);
  const [currentId, setCurrentId] = useState(productId);
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCurrentId(productId);
    if (productId) {
      setLoading(true);
      getProduct(productId)
        .then((p) => {
          setForm({
            name: p.name || "",
            name_en: p.name_en || "",
            description: p.description || "",
            category_id: p.category_id || "",
            unit_id: p.unit_id || "",
            state_id: p.state_id ?? "",
            price: p.price ?? "",
            quantity: p.quantity ?? "",
            status: p.status || "Active",
          });
          setImages(p.images || []);
        })
        .catch((err) => notify(`Error: ${err.message}`, { type: "error" }))
        .finally(() => setLoading(false));
    } else {
      setForm(emptyForm);
      setImages([]);
    }
  }, [productId, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!form.name || !form.description || !form.unit_id || form.price === "") {
      notify("Name, description, unit and price are required", { type: "warning" });
      return;
    }
    setSaving(true);
    try {
      const data = {
        name: form.name,
        name_en: form.name_en || undefined,
        description: form.description,
        category_id: form.category_id || undefined,
        unit_id: Number(form.unit_id),
        state_id: form.state_id === "" ? null : Number(form.state_id),
        price: Number(form.price),
        status: form.status,
      };
      if (form.quantity !== "" && form.quantity !== null && form.quantity !== undefined) {
        data.quantity = Number(form.quantity);
      }

      if (currentId) {
        await updateProduct(currentId, data);
        notify("Product saved", { type: "success" });
      } else {
        const created = await createProduct(data);
        notify("Product created. You can now add images.", { type: "success" });
        setCurrentId(created.id);
      }
      onSaved();
    } catch (err) {
      notify(`Error: ${err.message}`, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleAddImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (images.length + files.length > 5) {
      notify("A product can have at most 5 images", { type: "warning" });
      return;
    }
    setUploading(true);
    try {
      await uploadProductImages(currentId, files);
      const updated = await getProduct(currentId);
      setImages(updated.images || []);
      onSaved();
    } catch (err) {
      notify(`Error: ${err.message}`, { type: "error" });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemoveImage = async (index) => {
    try {
      await deleteProductImages(currentId, [index]);
      const updated = await getProduct(currentId);
      setImages(updated.images || []);
      onSaved();
    } catch (err) {
      notify(`Error: ${err.message}`, { type: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{currentId ? "Edit Product" : "Add Product"}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                fullWidth
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name (English)"
                fullWidth
                value={form.name_en}
                onChange={(e) => setForm((f) => ({ ...f, name_en: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                required
                multiline
                minRows={2}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={form.category_id}
                  onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Unit</InputLabel>
                <Select
                  label="Unit"
                  value={form.unit_id}
                  onChange={(e) => setForm((f) => ({ ...f, unit_id: e.target.value }))}
                >
                  {units.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  label="State"
                  value={form.state_id}
                  onChange={(e) => setForm((f) => ({ ...f, state_id: e.target.value }))}
                >
                  <MenuItem value="">None</MenuItem>
                  {states.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Price"
                type="number"
                fullWidth
                required
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Quantity (blank = unlimited)"
                type="number"
                fullWidth
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" gutterBottom>
                Images ({images.length}/5)
              </Typography>
              {images.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                  {images.map((img, idx) => (
                    <Box key={idx} position="relative">
                      <img
                        src={getImageUrl(img)}
                        alt=""
                        style={{ height: 80, width: 80, objectFit: "cover", borderRadius: 4 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveImage(idx)}
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          bgcolor: "background.paper",
                          boxShadow: 1,
                          "&:hover": { bgcolor: "error.light" },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              {currentId ? (
                images.length < 5 && (
                  <Button variant="outlined" component="label" disabled={uploading}>
                    {uploading ? "Uploading..." : "Add Images"}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif"
                      multiple
                      hidden
                      onChange={handleAddImages}
                    />
                  </Button>
                )
              ) : (
                <Typography variant="caption" color="text.secondary" display="block">
                  Save the product first to enable image upload.
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
          {currentId ? "Save Changes" : "Create Product"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeleteConfirmDialog = ({ open, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Delete Product</DialogTitle>
    <DialogContent>
      <Typography>This will mark the product Inactive, not remove it.</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button color="error" variant="contained" onClick={onConfirm}>
        Mark Inactive
      </Button>
    </DialogActions>
  </Dialog>
);

const SalatProductsPage = () => {
  const notify = useNotify();
  const { permissions } = usePermissions();
  const { categories } = useSalatCategories();
  const { units } = useUnits();
  const { states } = useStates();

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canEdit = canWrite(permissions);
  const canRemove = canDelete(permissions);

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const refetch = () => {
    setLoading(true);
    listProducts({
      page: page + 1,
      limit,
      status: statusFilter || undefined,
      category_id: categoryFilter || undefined,
      search: search || undefined,
    })
      .then((res) => {
        setProducts(res.data || []);
        setTotal(res.total || 0);
      })
      .catch((err) => notify(`Error: ${err.message}`, { type: "error" }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, statusFilter, categoryFilter, search]);

  const handleDelete = async () => {
    try {
      await deleteProduct(deleteTarget.id);
      notify("Product marked Inactive", { type: "success" });
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      notify(`Error: ${err.message}`, { type: "error" });
    }
  };

  const formatPrice = (price) => {
    const n = parseFloat(price);
    return isNaN(n) ? price : n.toLocaleString(undefined, { style: "currency", currency: "SDG" });
  };

  return (
    <Card sx={{ m: 2, p: 2 }}>
      <Title title="Salat Mahsool - Products" />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Salat Mahsool Products</Typography>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingId(null);
              setFormOpen(true);
            }}
          >
            Add Product
          </Button>
        )}
      </Box>

      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          label="Search name"
          size="small"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setPage(0);
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Status</TableCell>
                {(canEdit || canRemove) && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    {p.images?.[0] ? (
                      <img
                        src={getImageUrl(p.images[0])}
                        alt={p.name}
                        style={{ height: 40, width: 40, objectFit: "cover", borderRadius: 4 }}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{p._category?.name || "-"}</TableCell>
                  <TableCell>{formatPrice(p.price)}</TableCell>
                  <TableCell>{p._unit?.name || "-"}</TableCell>
                  <TableCell>{p._state?.name || "-"}</TableCell>
                  <TableCell>{p.quantity === null || p.quantity === undefined ? "∞" : p.quantity}</TableCell>
                  <TableCell>
                    <ProductStatusChip status={p.status} />
                  </TableCell>
                  {(canEdit || canRemove) && (
                    <TableCell align="right">
                      {canEdit && (
                        <IconButton
                          onClick={() => {
                            setEditingId(p.id);
                            setFormOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {canRemove && (
                        <IconButton onClick={() => setDeleteTarget(p)}>
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {products.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No products found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => {
              setLimit(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      {formOpen && (
        <ProductFormDialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          productId={editingId}
          categories={categories}
          units={units}
          states={states}
          onSaved={refetch}
        />
      )}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </Card>
  );
};

export default SalatProductsPage;
